import { GridCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { standardOperators, tagsOperators } from '../../Compounds/UpstackDataGrid/helpers';
import { WorkflowOrder } from '../../../Api/Orders/ordersApi';
import { OrderDetailsCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/OrderDetailsCTARenderer';
import { addressComponentColumns } from '../../Compounds/UpstackDataGrid/addressColumns';
import { tagsColumn } from '../../Compounds/UpstackDataGrid/tagColumns';
import { EntityTagRenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/EntityTagRenderer';
import { GroupedGridColDef } from '../../Compounds/UpstackDataGrid/gridColumnTypes';
import type { SupportedTagEntities } from '../../../store/Tags';
import { SupportedTagEntityTypes } from '../../../store/tagEntityTypes';
import {
  formatDate,
  formatIps,
  formatOrderLocations,
  formatSiteContact,
  orderDeliveryDetails,
  orderLocationCode,
  orderLocationName
} from './orderRequestDetailFields';

// Lazy on purpose: a static import closes the col-def ↔ store/User cycle and hands out a half-built store.
const refetchOrders = async (): Promise<void> =>
  (await import('../../../store/WorkflowOrder')).fetchWorkflowOrders(true, {
    showSpinner: false,
    afterSettledWrite: true
  });

const detailsColumn: GridSingleSelectColDef = {
  field: 'details',
  headerName: 'Details',
  sortable: false,
  width: 65,
  filterable: false,
  type: 'singleSelect',
  valueOptions: [],
  renderCell: (params: GridCellParams) => <OrderDetailsCTARenderer data={params} />,
  hideable: false,
  disableReorder: true
};

// Optional per-location code/name: what the requester typed for NEW locations, with an
// SF fallback for existing locations (see orderLocationCode/Name); off by default like
// the address columns.
const locationFieldColumn = (
  field: string,
  headerName: string,
  getValue: (order: WorkflowOrder) => string
): GroupedGridColDef => ({
  field,
  headerName,
  width: 180,
  filterable: true,
  type: 'singleSelect',
  valueOptions: [],
  filterOperators: standardOperators(),
  valueGetter: (_, row) => getValue(row as WorkflowOrder),
  description: 'Drag to move column',
  defaultHidden: true
});

const locationFieldColumns: GroupedGridColDef[] = [
  locationFieldColumn('location_code', 'Location Code', orderLocationCode),
  locationFieldColumn('location_name', 'Location Name', orderLocationName)
];

// A row is whichever Salesforce record surfaced it, so its tags hang off that record's type.
const orderTagEntityType = (row: SupportedTagEntities): SupportedTagEntityTypes =>
  (row as WorkflowOrder).source_type === 'project'
    ? SupportedTagEntityTypes.PROJECT
    : SupportedTagEntityTypes.OPPORTUNITY;

// Order rows carry the Salesforce id in `id`; tag relations key on the portal id.
const orderTagEntityId = (row: SupportedTagEntities): string => (row as WorkflowOrder).upstack_id;

// Already the portal account id on both row kinds, because each is serialized from its record's `account`.
const orderTagAccountId = (row: SupportedTagEntities): string | undefined =>
  (row as WorkflowOrder).account_id ?? undefined;

export const WorkflowBaseColumns: GroupedGridColDef[] = [
  {
    field: 'request_number',
    headerName: 'Request Number',
    width: 150,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators()
  },
  {
    field: 'created_at',
    headerName: 'Date of Request',
    width: 150,
    type: 'singleSelect',
    filterable: true,
    valueFormatter: (value) => formatDate(value)
  },
  {
    field: 'request_type',
    headerName: 'Type of Request',
    width: 160,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators()
  },
  {
    field: 'service_type',
    headerName: 'Type of Service',
    width: 220,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators()
  },
  {
    field: 'location',
    headerName: 'Location',
    width: 260,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueGetter: (_, row) => formatOrderLocations(row as WorkflowOrder),
    valueFormatter: (value) => value
  },
  ...addressComponentColumns(standardOperators()),
  ...locationFieldColumns,
  {
    field: 'request_status',
    headerName: 'Request Status',
    width: 180,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators()
  },
  {
    field: 'preferred_term',
    headerName: 'Preferred Term',
    width: 150,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators()
  },
  {
    field: 'desired_install_date',
    headerName: 'Desired Install Date',
    width: 150,
    filterable: true,
    type: 'singleSelect',
    valueGetter: (_, row) => (row as WorkflowOrder).advanced_fields?.desired_install_date,
    valueFormatter: (value) => formatDate(value)
  },
  {
    field: 'ip_details',
    headerName: 'IP Details',
    width: 180,
    filterable: true,
    type: 'singleSelect',
    filterOperators: standardOperators(),
    valueGetter: (_, row) =>
      (row as WorkflowOrder).advanced_fields?.ip_requirements || formatIps((row as WorkflowOrder).advanced_fields?.ips)
  },
  {
    field: 'diversity_requirements',
    headerName: 'Diversity Requirements',
    width: 200,
    filterable: true,
    type: 'singleSelect',
    filterOperators: standardOperators(),
    valueGetter: (_, row) => (row as WorkflowOrder).advanced_fields?.diversifying_from
  },
  {
    field: 'handoff_requirements',
    headerName: 'Handoff Requirement',
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    valueGetter: (_, row) => (row as WorkflowOrder).advanced_fields?.handoff_requirement
  },
  {
    field: 'site_contact',
    headerName: 'Site Contact',
    width: 220,
    filterable: true,
    type: 'singleSelect',
    filterOperators: standardOperators(),
    valueGetter: (_, row) => formatSiteContact((row as WorkflowOrder).advanced_fields?.site_contact)
  },
  {
    field: 'location_delivery_details',
    headerName: 'Location and Delivery Details',
    width: 260,
    filterable: true,
    type: 'singleSelect',
    filterOperators: standardOperators(),
    valueGetter: (_, row) => orderDeliveryDetails((row as WorkflowOrder).advanced_fields)
  },
  {
    field: 'existing_circuit_details',
    headerName: 'Existing Circuit Details',
    width: 240,
    filterable: true,
    type: 'singleSelect',
    filterOperators: standardOperators(),
    valueGetter: (_, row) => (row as WorkflowOrder).advanced_fields?.existing_circuit_details,
    defaultHidden: true
  },
  tagsColumn({
    filterOperators: tagsOperators(),
    renderer: EntityTagRenderer,
    entityType: orderTagEntityType,
    entityId: orderTagEntityId,
    entityAccountId: orderTagAccountId,
    refetch: refetchOrders
  })
];

// The Details CTA first, then the shared column set
export const WorkflowColumnsWithDetails: GridSingleSelectColDef[] = [detailsColumn, ...WorkflowBaseColumns];

// The shared column set, without the Details CTA
export const WorkflowColumnsWithoutDetails: GridSingleSelectColDef[] = [...WorkflowBaseColumns];
